'use client';

import {
    Building2,
    File,
    Globe,
    Mail,
    MapPin,
    Phone,
    ShoppingBasket,
} from 'lucide-react';
import Link from 'next/link';
import Script from 'next/script';
import { useState } from 'react';
import MapComponent from '@/components/layout/Map';
import PageHeader, {
    COMPACT_PAGE_HEADER_HEIGHT,
} from '@/components/layout/PageHeader';
import AnimateIn from '@/components/ui/AnimateIn';
import AppImage from '@/components/ui/AppImage';
import CTA from '@/components/ui/CTA';
import { trackEvent } from '@/lib/analytics';
import { toAbsoluteAssetUrl } from '@/lib/metadata/seo';
import { getBlurDataURL } from '@/lib/shimmer';

function MapEmbed({ address }) {
    const [loadError, setLoadError] = useState(false);
    const src = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

    if (loadError) {
        return (
            <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                >
                    View on Google Maps
                </a>
            </div>
        );
    }

    return (
        <iframe
            src={src}
            width="100%"
            height="200"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Map of ${address}`}
            onError={() => setLoadError(true)}
        />
    );
}

// Sales team data with full bios and photos
const salesTeam = [
    {
        name: 'Jag Grewal',
        role: 'North/West Auckland & Northland Territory Manager',
        phone: '021 863 845',
        email: 'jagdish@trenchmate.co.nz',
        areas: 'North Auckland, West Auckland, Northland',
        image: 'team/Jag.webp',
        bio: 'With three years of hands-on experience as a Trenchmate truck driver under his belt, Jag understands what it takes to keep sites running smoothly for customers. His experience gave him practical insight into how jobs operate on the ground and what clients need delivered day to day - it also gave him a strong understanding of how different Trenchmate gear can be used to solve tricky situations. Jag is known for clear communication, problem-solving in challenging situations, and making sure crews have the right shoring equipment to stay safe and efficient.',
    },
    {
        name: 'Symon Maxwell',
        role: 'South/East Auckland & Northern Waikato Territory Manager',
        phone: '021 863 614',
        email: 'symon@trenchmate.co.nz',
        areas: 'South Auckland, East Auckland, Northern Waikato',
        image: 'team/Symon.webp',
        bio: "Symon joined Trenchmate in early 2024 – as a former customer, he already knew Trenchmate's products were something special. Drawn by the company's Kiwi-made innovation and strong reputation, he now helps customers across South Auckland and Waikato find the right solutions for their projects. Symon's practical, hands-on approach and \"do what I say I will do\" philosophy make him a trusted partner on site. He's particularly proud of the Siltmate range, where every job brings a new challenge and result to be proud of.",
    },
    {
        name: 'Rich Waratini',
        role: 'Central North Island Territory Manager',
        phone: '021 863 603',
        email: 'rich@trenchmate.co.nz',
        areas: 'Waikato, Tauranga, Bay of Plenty, Central North Island',
        image: 'team/Rich.webp',
        bio: "With more than 15 years at Trenchmate – and nearly a decade in the hire and civil industries before that – Rich knows this business inside out. His background in drainlaying, machinery hire, and directional drilling gives him a real understanding of what customers need on the ground. He's known for his quick thinking, clear communication, and commitment to getting the right product to site, fast. Whether it's a large infrastructure project or a smaller custom job, Rich thrives on building long-term relationships and solving challenges with practical, proven solutions.",
    },
    {
        name: 'Steve Hood',
        role: 'South Island & Wellington Territory Manager',
        phone: '021 863 597',
        email: 'steve.h@trenchmate.co.nz',
        areas: 'Wellington, South Island',
        image: 'team/Steve.webp',
        bio: "As a qualified certifying drainlayer who owned his own plumbing and drainage company for over a decade, Steve brings deep, first-hand knowledge to his role at Trenchmate. Having used Trenchmate equipment himself before joining the team, he understands exactly what works in the field. Over the past few years, Steve has built Trenchmate's South Island client base from the ground up, delivering dependable service and tailored support for every project. His approach is simple – under promise and over deliver – and it's one that's earned him lasting trust from customers throughout the region.",
    },
];

const mainOfficeContacts = [
    {
        name: 'Steve Wyllie',
        role: 'General Manager',
        phone: '021 863 479',
        email: 'stevew@trenchmate.co.nz',
    },
    {
        name: 'Mark Gover',
        role: 'Water Solutions',
        phone: '021 199 7655',
        email: 'mark@trenchmate.co.nz',
    },
    {
        name: 'Jodi Goodall',
        role: 'Business Manager',
        phone: '021 363 397',
        email: 'jodi@trenchmate.co.nz',
    },
    {
        name: 'Maia Ioane',
        role: 'Despatch Manager',
        phone: '021 684 542',
        email: 'despatch@trenchmate.co.nz',
    },
];

const locations = [
    {
        name: 'Trenchmate Christchurch',
        address: '92 Shands Road, Hornby, Christchurch',
        href: 'https://maps.app.goo.gl/7iZciRsbQqU4Kg6V6',
        phone: '03 348 1234',
        hours: 'Mon-Fri, 7:00 AM - 5:00 PM',
    },
    {
        name: 'Mcleod Hiabs',
        address: '75 Curly Mcleod Way, Mt Maunganui, Tauranga',
        href: 'https://maps.app.goo.gl/xAAEKCaymSL9AG5z8',
        phone: '07 575 5678',
        hours: 'Mon-Fri, 7:30 AM - 4:30 PM',
    },
    {
        name: 'Allan Shaw Carrying',
        address: '18 Birmingham Street, Paraparaumu, Wellington',
        href: 'https://maps.app.goo.gl/hRNRbsmcdhsFbtRx7',
        phone: '04 298 9012',
        hours: 'Mon-Fri, 8:00 AM - 5:00 PM',
    },
];

// Helper to get responsive team image path
const getTeamImageSrc = (imagePath) => {
    // Use the medium variant (768px) as the base - good balance for mobile/desktop
    // Original full-res images are 1667x2500px which is overkill for 192px display
    const baseName = imagePath.replace('.webp', '');
    return `${baseName}-medium.webp`;
};

const SalesTeamImage = ({ member, imageLoaded, onLoad }) => (
    <AppImage
        src={`/${getTeamImageSrc(member.image)}`}
        alt={`${member.name} - ${member.role}`}
        fill
        className={`object-cover object-[center_15%] sm:object-top transition-opacity duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        sizes="(max-width: 640px) 100vw, 192px"
        loading="lazy"
        placeholder="blur"
        blurDataURL={getBlurDataURL(16, 16)}
        onLoad={onLoad}
        skipFadeIn
    />
);

// Sales Team Card Component - clean design with full bio
const SalesTeamCard = ({ member }) => {
    const [bioExpanded, setBioExpanded] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
        <div className="bg-white border-t-4 border-t-brand border-x border-b border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden h-full flex flex-col">
            <div className="flex flex-col sm:flex-row flex-1">
                {/* Photo */}
                <div className="sm:w-48 sm:shrink-0">
                    <div className="relative w-full h-72 sm:h-full sm:min-h-80 bg-gray-100">
                        {/* Loading skeleton */}
                        {!imageLoaded && (
                            <div
                                className="absolute inset-0 bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 motion-safe:animate-pulse"
                                aria-hidden="true"
                            />
                        )}
                        <SalesTeamImage
                            member={member}
                            imageLoaded={imageLoaded}
                            onLoad={() => setImageLoaded(true)}
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-6 flex flex-col">
                    {/* Header */}
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {member.name}
                        </h3>
                        <p className="text-brand-dark font-bold text-sm">
                            {member.role}
                        </p>
                    </div>

                    {/* Contact Info - Prominent */}
                    <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                        <Link
                            href={`tel:${member.phone}`}
                            className="flex items-center gap-2 text-gray-900 hover:text-brand hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded transition-colors"
                            onClick={() =>
                                trackEvent('phone_call', {
                                    source: 'contact_page',
                                    target: 'sales_team',
                                    member_name: member.name,
                                })
                            }
                        >
                            <Phone className="w-5 h-5 shrink-0 text-gray-900" />
                            <span className="font-bold text-lg">
                                {member.phone}
                            </span>
                        </Link>
                        <Link
                            href={`mailto:${member.email}`}
                            className="flex items-center gap-2 text-gray-900 hover:text-brand hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded transition-colors"
                            onClick={() =>
                                trackEvent('email_click', {
                                    source: 'contact_page',
                                    target: 'sales_team',
                                    member_name: member.name,
                                })
                            }
                        >
                            <Mail className="w-5 h-5 shrink-0 text-gray-900" />
                            <span className="font-medium">{member.email}</span>
                        </Link>
                        <div className="flex items-start gap-2 text-gray-700">
                            <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-gray-700" />
                            <span>{member.areas}</span>
                        </div>
                    </div>

                    {/* Bio Section - Truncated when cards stacked vertically, full on 2-column grid */}
                    <div className="flex-1">
                        {/* Desktop (2-column grid): show full bio */}
                        <p className="hidden xl:block text-gray-600 text-sm leading-relaxed">
                            {member.bio}
                        </p>

                        {/* Single column: truncated with animated read more */}
                        <div className="xl:hidden">
                            <div
                                className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${
                                    bioExpanded ? 'max-h-125' : 'max-h-[4.5em]'
                                }`}
                            >
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    {member.bio}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setBioExpanded(!bioExpanded)}
                                className="mt-2 text-brand-dark hover:text-brand hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                                aria-label={
                                    bioExpanded
                                        ? `Read less about ${member.name}`
                                        : `Read more about ${member.name}`
                                }
                                aria-expanded={bioExpanded}
                            >
                                {bioExpanded ? 'Read less' : 'Read more'}
                                <svg
                                    className={`w-4 h-4 transition-transform duration-300 ${bioExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContactPage = () => {
    // Build LocalBusiness structured data for the head office + other locations
    const locationImageUrl = toAbsoluteAssetUrl('/logo/trenchmate-logo.svg');
    const structuredLocations = [
        {
            '@type': 'LocalBusiness',
            name: 'Trenchmate - Head Office',
            telephone: '+648000385274',
            address: {
                '@type': 'PostalAddress',
                streetAddress: '149 Boundary Road',
                addressLocality: 'Papakura',
                addressRegion: 'Auckland',
                postalCode: '2110',
                addressCountry: 'NZ',
            },
            openingHoursSpecification: [
                {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: [
                        'Monday',
                        'Tuesday',
                        'Wednesday',
                        'Thursday',
                        'Friday',
                    ],
                    opens: '07:00',
                    closes: '17:00',
                },
            ],
            url: 'https://www.trenchmate.co.nz',
            image: locationImageUrl,
        },
        ...locations.map((loc) => ({
            '@type': 'LocalBusiness',
            name: loc.name,
            telephone: loc.phone ? loc.phone : '+64',
            address: {
                '@type': 'PostalAddress',
                streetAddress: loc.address,
                addressLocality: '',
                addressRegion: '',
                postalCode: '',
                addressCountry: 'NZ',
            },
            url: loc.href || '',
            image: locationImageUrl,
        })),
    ];

    const structuredData = JSON.stringify(structuredLocations);

    return (
        <div className="min-h-screen bg-white">
            {/* Inject structured data for branch locations */}
            <Script
                id="contact-locations-schema"
                type="application/ld+json"
                strategy="afterInteractive"
            >
                {structuredData}
            </Script>
            {/* Page header */}
            <PageHeader
                title="Contact Us"
                description="Connect with your local Trenchmate representative for personalised support and expert advice across New Zealand."
                height={COMPACT_PAGE_HEADER_HEIGHT}
                showScrollButton={false}
                backgroundImage={'/images/IMG_3875.webp'}
                backgroundAlt="Trenchmate Contact Us"
                bgBlurDataURL={getBlurDataURL(16, 9)}
                breadcrumbs={[
                    { label: 'Home', href: '/' },
                    { label: 'Contact Us' },
                ]}
            />

            {/* Quick Contact Options */}
            <section className="py-16 bg-white">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-bold text-gray-900">
                            Get In Touch
                        </h2>
                        <div className="w-24 h-1 bg-brand mx-auto mt-4" />
                        <p className="text-gray-600 mt-6 max-w-2xl mx-auto">
                            Our team is ready to assist you with any inquiries.
                            Reach out through any of these channels for prompt
                            assistance.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <Link
                            href="tel:0800385274"
                            className="p-8 bg-white border-t-4 border-t-brand border-x border-b border-gray-200 shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 transition-all duration-300 group cursor-pointer block"
                            onClick={() =>
                                trackEvent('phone_call', {
                                    source: 'contact_page',
                                    target: 'main_line',
                                })
                            }
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center shrink-0 group-hover:bg-gray-200 transition-colors">
                                    <Phone className="w-7 h-7 text-brand" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Customer Support
                                </h3>
                            </div>
                            <p className="text-gray-900 text-lg font-medium group-hover:text-brand transition-colors duration-200">
                                0800 385 274
                            </p>
                            <p className="text-gray-600 mt-4">
                                Reach out for general queries or on-site support
                            </p>
                        </Link>

                        <a
                            href="#locations"
                            className="p-8 bg-white border-t-4 border-t-brand border-x border-b border-gray-200 shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 transition-all duration-300 group cursor-pointer block"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center shrink-0 group-hover:bg-gray-200 transition-colors">
                                    <Building2 className="w-7 h-7 text-brand" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Head Office
                                </h3>
                            </div>
                            <p className="text-gray-900 mb-1 group-hover:text-brand transition duration-200">
                                149 Boundary Road, Papakura
                            </p>
                            <p className="text-gray-900 mb-4">Auckland 2110</p>
                            <p className="text-gray-600">Mon-Fri, 7am-5pm</p>
                        </a>

                        <a
                            href="#sales-team"
                            className="p-8 bg-white border-t-4 border-t-brand border-x border-b border-gray-200 shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 transition-all duration-300 group cursor-pointer block"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center shrink-0 group-hover:bg-gray-200 transition-colors">
                                    <Globe className="w-7 h-7 text-brand" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    National Coverage
                                </h3>
                            </div>
                            <p className="text-gray-900 mb-4 group-hover:text-brand transition-colors">
                                Meet our Team
                            </p>
                            <p className="text-gray-600">
                                Local representatives across New Zealand
                            </p>
                        </a>
                    </div>

                    {/* Inquiry submit slide-over is mounted globally in the Navbar */}
                </div>
            </section>

            {/* Meet our Team Section */}
            <section
                id="sales-team"
                className="py-16 bg-gray-50"
                style={{
                    scrollMarginTop:
                        'calc(80px + var(--announcement-offset, 0px))',
                }}
            >
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <AnimateIn>
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                Meet our Team
                            </h2>
                        </AnimateIn>
                        <div className="w-24 h-1 bg-brand mx-auto mb-6" />
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Our experienced territory managers are here to
                            provide expert advice and personalised support for
                            your projects across New Zealand
                        </p>
                    </div>

                    <AnimateIn delay={0.1}>
                        <div className="flex flex-wrap justify-center gap-8 items-stretch">
                            {salesTeam.map((member, index) => (
                                <div
                                    key={index}
                                    className="w-full xl:w-[calc(50%-1rem)]"
                                >
                                    <SalesTeamCard member={member} />
                                </div>
                            ))}
                        </div>
                    </AnimateIn>
                </div>
            </section>

            {/* Head Office Team Section */}
            <section className="py-16 bg-white">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <AnimateIn>
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                Head Office Team
                            </h2>
                        </AnimateIn>
                        <div className="w-24 h-1 bg-brand mx-auto mb-6" />
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Meet our dedicated leadership team committed to
                            providing exceptional service and support
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {mainOfficeContacts.map((contact, index) => (
                            <div
                                key={index}
                                className="bg-white border-t-4 border-t-brand border-x border-b border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col h-full"
                            >
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="mb-4">
                                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                                            {contact.name}
                                        </h3>
                                        <p className="text-brand-dark font-semibold text-sm">
                                            {contact.role}
                                        </p>
                                    </div>
                                    <div className="space-y-3 mt-auto">
                                        <Link
                                            href={`tel:${contact.phone}`}
                                            className="flex items-center gap-2 text-gray-700 hover:text-brand hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded transition-colors"
                                        >
                                            <Phone className="w-5 h-5 shrink-0" />
                                            <span className="font-medium">
                                                {contact.phone}
                                            </span>
                                        </Link>
                                        <Link
                                            href={`mailto:${contact.email}`}
                                            className="flex items-center gap-2 text-gray-700 hover:text-brand hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded transition-colors"
                                        >
                                            <Mail className="w-5 h-5 shrink-0" />
                                            <span className="text-sm">
                                                {contact.email}
                                            </span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section
                id="locations"
                className="py-16 bg-gray-100"
                style={{
                    scrollMarginTop:
                        'calc(80px + var(--announcement-offset, 0px))',
                }}
            >
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <AnimateIn>
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Find Our Locations
                            </h2>
                        </AnimateIn>
                        <div className="w-24 h-1 bg-brand mx-auto mb-6" />
                    </div>

                    {/* Headquarters Card */}
                    <div className="bg-white overflow-hidden shadow-md border border-gray-200 mb-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="p-8">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">
                                    Trenchmate Headquarters
                                </h3>
                                <div className="space-y-4">
                                    <Link
                                        href="https://www.google.com/maps?q=149+Boundary+Road,+Papakura,+Auckland+2110,+New+Zealand"
                                        className="flex items-start transition duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <MapPin className="w-6 h-6 text-brand group-hover:text-brand transition-colors mr-3 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-medium text-gray-900 group-hover:text-brand transition-colors">
                                                149 Boundary Road
                                            </p>
                                            <p className="text-gray-600 group-hover:text-brand transition-colors">
                                                Papakura, Auckland 2110
                                            </p>
                                            <p className="text-gray-600 group-hover:text-brand transition-colors">
                                                New Zealand
                                            </p>
                                        </div>
                                    </Link>

                                    {/* open phone */}
                                    <Link
                                        href="tel:0800385274"
                                        className="flex items-start transition duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded"
                                    >
                                        <Phone className="w-6 h-6 text-brand group-hover:text-brand transition-colors mr-3 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-medium text-gray-900 group-hover:text-brand transition-colors">
                                                0800 385 274
                                            </p>
                                            <p className="text-gray-600 group-hover:text-brand transition-colors">
                                                Support Line
                                            </p>
                                        </div>
                                    </Link>

                                    <Link
                                        href={`mailto:admin@trenchmate.co.nz`}
                                        className="flex items-start transition duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded"
                                        onClick={() =>
                                            trackEvent('email_click', {
                                                source: 'contact_page',
                                                target: 'admin',
                                            })
                                        }
                                    >
                                        <Mail className="w-6 h-6 text-brand group-hover:text-brand transition-colors mr-3 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-medium text-gray-900 group-hover:text-brand transition-colors">
                                                admin@trenchmate.co.nz
                                            </p>
                                            <p className="text-gray-600 group-hover:text-brand transition-colors">
                                                General Inquiries
                                            </p>
                                        </div>
                                    </Link>

                                    <div className="pt-4">
                                        <h4 className="font-bold text-gray-900 mb-2">
                                            Business Hours
                                        </h4>
                                        <div className="flex justify-between max-w-xs">
                                            <span className="text-gray-600">
                                                Monday - Friday
                                            </span>
                                            <span className="font-medium text-gray-900">
                                                7:00 AM - 5:00 PM
                                            </span>
                                        </div>
                                        <div className="flex justify-between max-w-xs">
                                            <span className="text-gray-600">
                                                Saturday - Sunday
                                            </span>
                                            <span className="font-medium text-gray-900">
                                                Closed
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-200 h-96 lg:h-auto">
                                {/* Map placeholder - would be replaced with actual map component */}
                                <div className="w-full h-full flex items-center justify-center bg-linear-to-r from-gray-100 to-gray-200">
                                    <MapComponent />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                        {locations.map((location, index) => (
                            <div
                                key={index}
                                className="bg-white overflow-hidden shadow-md border border-gray-200 flex flex-col h-full"
                            >
                                <div className="p-5 lg:p-6 flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                                        {location.name}
                                    </h3>

                                    <div className="space-y-3 text-sm text-gray-700">
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                                            <div>
                                                <p className="font-medium text-gray-900 leading-tight">
                                                    {location.address}
                                                </p>
                                            </div>
                                        </div>

                                        <Link
                                            href={`tel:${location.phone}`}
                                            className="flex items-start gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded"
                                        >
                                            <Phone className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                                            <div>
                                                <p className="font-medium text-gray-900">
                                                    {location.phone}
                                                </p>
                                                <p className="text-gray-600 text-sm">
                                                    {location.hours}
                                                </p>
                                            </div>
                                        </Link>
                                    </div>
                                </div>

                                {/* Map moved to bottom to avoid clipping and provide better layout */}
                                <div className="bg-gray-100 mt-auto">
                                    <div className="w-full h-40 sm:h-48 md:h-44 lg:h-40">
                                        <MapEmbed address={location.address} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Areas We Serve */}
            <section id="areas-we-serve" className="bg-gray-50 py-16">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-bold text-gray-900">
                            Areas We Serve
                        </h2>
                        <div className="w-24 h-1 bg-brand mx-auto mt-4 mb-6" />
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Based in Papakura, Auckland, we deliver trench
                            shields and shoring equipment to construction sites
                            across New Zealand. We own our trucks, so we control
                            the delivery.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                region: 'Auckland',
                                detail: 'Papakura, Manukau, Central Auckland, North Shore, West Auckland, Pukekohe',
                            },
                            {
                                region: 'Rodney District',
                                detail: 'Warkworth, Snells Beach, Matakana, Wellsford, Helensville, Silverdale',
                            },
                            {
                                region: 'Waikato & Bay of Plenty',
                                detail: 'Hamilton, Cambridge, Tauranga, Rotorua — served by our local reps',
                            },
                            {
                                region: 'Northland',
                                detail: 'Whangarei, Kerikeri, Dargaville — planned delivery scheduling',
                            },
                            {
                                region: 'Wellington & South Island',
                                detail: 'Served via our Paraparaumu and Christchurch partner depots',
                            },
                            {
                                region: 'Nationwide',
                                detail: 'For any project anywhere in NZ — contact us to discuss delivery',
                            },
                        ].map((area) => (
                            <div
                                key={area.region}
                                className="p-6 bg-white border border-gray-200 shadow-sm flex flex-col gap-2"
                            >
                                <div className="flex items-center gap-2">
                                    <MapPin className="text-brand w-4 h-4 shrink-0" />
                                    <h3 className="font-bold text-gray-900">
                                        {area.region}
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-600">
                                    {area.detail}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CTA
                heading="Explore Our Product Range"
                subtext="Our products are designed to provide safety, efficiency, and reliability in all trenching and shoring applications."
                primaryButton={{
                    href: '/products',
                    label: 'Our Products',
                    icon: <ShoppingBasket className="w-5 h-5 mr-2" />,
                    trackingLabel: 'Our Products',
                }}
                secondaryButton={{
                    href: '/resources',
                    label: 'View Resources',
                    icon: <File className="w-5 h-5 mr-2" />,
                    trackingLabel: 'View Resources',
                }}
                ctaSection="contact_bottom_cta"
            />
        </div>
    );
};

export default ContactPage;
